import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  MapPin, Bell, ChevronDown, ChevronRight, Home, Building2, Image as ImageIcon, Clock,
  CalendarDays, Tag, Ticket, Star, BarChart3, Settings, Crown, BadgeCheck, Pencil,
  ImagePlus, Tag as TagIcon, Eye, Heart, Phone, Globe, Info, Plus, X, Zap, Sparkles,
  Share2, MessageSquare, Menu, ExternalLink, MoreHorizontal, User, LogOut, Trash2, Check,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DeleteAccountModal } from "@/components/DeleteAccountModal";
import { useLanguage } from "@/i18n/LanguageContext";
import { PhotosView } from "@/components/pro/PhotosView";
import { HorairesView } from "@/components/pro/HorairesView";
import { PromotionsView } from "@/components/pro/PromotionsView";
import { ApercuView } from "@/components/pro/ApercuView";
import { EventsView } from "@/components/pro/EventsView";
import { ReservationsView } from "@/components/pro/ReservationsView";
import { AvisView } from "@/components/pro/AvisView";
import { StatsView } from "@/components/pro/StatsView";
import { ClaimView } from "@/components/pro/ClaimView";
import { ContactAdminView } from "@/components/pro/ContactAdminView";
import { SettingsView } from "@/components/pro/SettingsView";

const SECTION_TITLES = {
  apercu: ["Aperçu", "Vue d'ensemble de ton établissement et de son activité."],
  etablissement: ["Mon établissement", "Gérez les informations, performances et activités de votre établissement."],
  photos: ["Photos", "Gère la galerie de ta page publique."],
  horaires: ["Horaires", "Définis tes horaires d'ouverture."],
  evenements: ["Événements", "Créez et gérez les événements de votre établissement."],
  promotions: ["Promotions", "Crée des offres visibles sur ta page publique."],
  reservations: ["Réservations", "Suis toutes les réservations de tes événements."],
  avis: ["Avis", "Lis et réponds aux avis de tes clients."],
  stats: ["Statistiques", "Analyse la performance de ton établissement."],
  contact: ["Contacter l'admin", "Écris à l'équipe GoSpot et suis ses réponses."],
  parametres: ["Paramètres & forfait", "Gère ton compte, ta sécurité et tes préférences."],
};

const SECTION_PATHS = {
  apercu: "apercu",
  etablissement: "etablissement",
  photos: "photos",
  horaires: "horaires",
  evenements: "evenements",
  promotions: "promotions",
  reservations: "reservations",
  avis: "avis",
  stats: "statistiques",
  contact: "contact-admin",
  parametres: "parametres",
};
const PATH_TO_SECTION = Object.fromEntries(Object.entries(SECTION_PATHS).map(([k, v]) => [v, k]));

const PRO_NAV = [
  { label: "Accueil", to: "/" },
  { label: "Explorer", to: "/explorer" },
  { label: "Ce soir", to: "/ce-soir" },
  { label: "Événements", to: "/evenements" },
  { label: "Hébergements", to: "/hebergements" },
  { label: "Favoris", to: "/favoris" },
];

const MENU = [
  { key: "apercu", label: "Aperçu", icon: Home },
  { key: "etablissement", label: "Mon établissement", icon: Building2 },
  { key: "photos", label: "Photos", icon: ImageIcon },
  { key: "horaires", label: "Horaires", icon: Clock },
  { key: "evenements", label: "Événements", icon: CalendarDays },
  { key: "promotions", label: "Promotions", icon: Tag },
  { key: "reservations", label: "Réservations", icon: Ticket },
  { key: "avis", label: "Avis", icon: Star, badge: 12 },
  { key: "stats", label: "Statistiques", icon: BarChart3 },
  { key: "contact", label: "Contacter l'admin", icon: MessageSquare },
  { key: "parametres", label: "Paramètres & forfait", icon: Settings },
];

const KPI_ICONS = { vues: Eye, reservations: CalendarDays, favoris: Heart, avis: Star };
const KPI_COLORS = { vues: ["#7C3AED", "#F5F3FF"], reservations: ["#16A34A", "#F0FDF4"], favoris: ["#EF4444", "#FEF2F2"], avis: ["#F59E0B", "#FFFBEB"] };

const Stars = ({ value }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-3.5 w-3.5 ${i <= value ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-200"}`} />
    ))}
  </span>
);

const Card = ({ children, className = "", testId }) => (
  <div data-testid={testId} className={`min-w-0 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm ${className}`}>
    {children}
  </div>
);

const ProMenuItem = ({ icon: Icon, label, testId, onClick, danger }) => {
  const { t } = useLanguage();
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition ${
        danger ? "text-red-600 hover:bg-red-50" : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      <Icon className={`h-4 w-4 ${danger ? "text-red-500" : "text-slate-400"}`} />
      {t(label)}
    </button>
  );
};

export default function ProDashboardPage() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const { section } = useParams();
  const [data, setData] = useState(null);
  const active = PATH_TO_SECTION[section] || "etablissement";
  const [lightbox, setLightbox] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [editInfo, setEditInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({ phone: "", website: "", address: "", description: "", category_label: "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [profileMenu, setProfileMenu] = useState(false);
  const [langueOpen, setLangueOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!profileMenu) return;
    const close = () => {
      setProfileMenu(false);
      setLangueOpen(false);
    };
    const onDown = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileMenu]);

  const goMenu = (to) => {
    setProfileMenu(false);
    setLangueOpen(false);
    navigate(to);
  };

  const goPlan = () => {
    setProfileMenu(false);
    setActive("parametres");
    setTimeout(() => document.querySelector('[data-testid="settings-plan"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 800);
  };

  const doLogout = async () => {
    setProfileMenu(false);
    navigate("/");
    await logout();
    toast.success(t("Déconnexion réussie"));
  };

  const setActive = (key) => navigate(`/pro/${SECTION_PATHS[key] || key}`);

  const load = () =>
    api
      .get("/pro/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 401) navigate("/connexion");
      });

  useEffect(() => {
    if (user === false) navigate("/connexion");
    else if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (section && !PATH_TO_SECTION[section]) navigate("/pro/etablissement", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const submitReply = async (reviewId) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/reviews/${reviewId}/reply`, { text: replyText });
      toast.success(t("Réponse publiée"));
      setReplyFor(null);
      setReplyText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const scrollToReviews = () => {
    setActive("etablissement");
    setTimeout(() => document.querySelector("[data-testid='pro-reviews-card']")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };

  const openInfoEdit = () => {
    if (!data?.establishment) return;
    const e = data.establishment;
    setInfoForm({
      phone: e.phone || "",
      website: e.website || "",
      address: e.address || "",
      description: e.description || "",
      category_label: e.category || "",
    });
    setEditInfo(true);
  };

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await api.patch("/pro/establishment", infoForm);
      toast.success(t("Informations mises à jour"));
      setEditInfo(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingInfo(false);
    }
  };

  if (!data) {
    return (
      <main className="mx-auto max-w-[1800px] px-4 py-10">
        <div className="h-[70vh] animate-pulse rounded-3xl bg-white" />
      </main>
    );
  }

  if (!data.establishment) {
    return <ClaimView onDone={load} />;
  }

  const est = data.establishment;
  const photos = est.gallery?.length ? est.gallery : [est.image];

  const sharePage = async () => {
    const url = `${window.location.origin}/etablissement/${est.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("Lien de ta page copié !"));
    } catch {
      toast.info(url);
    }
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex flex-col items-center pt-2 text-center">
        <img src={est.image} alt={est.name} className="h-20 w-20 rounded-full border-4 border-white object-cover shadow-md" />
        <h2 className="mt-3 flex items-center gap-1.5 text-lg font-extrabold text-slate-900">
          {est.name}
          <BadgeCheck className="h-4 w-4 fill-[#2563EB] text-white" />
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">{est.category}</p>
        <p className="text-xs text-slate-400">{est.city}</p>
        <Link
          to={`/etablissement/${est.slug}`}
          data-testid="public-page-link"
          className="mt-2.5 flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline"
        >
          {t("Voir ma page publique")}
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      <nav className="mt-6 flex-1 space-y-1" data-testid="pro-menu">
        {MENU.map((m) => (
          <button
            key={m.key}
            type="button"
            data-testid={`pro-menu-${m.key}`}
            onClick={() => {
              setActive(m.key);
              setDrawer(false);
            }}
            className={`flex w-full items-center justify-between rounded-2xl px-3.5 py-2.5 text-sm transition ${
              active === m.key ? "bg-[#EFF6FF] font-semibold text-[#2563EB]" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <span className="flex items-center gap-2.5">
              <m.icon className={`h-4 w-4 ${active === m.key ? "text-[#2563EB]" : "text-slate-400"}`} />
              {t(m.label)}
            </span>
            {m.badge && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#2563EB] px-1 text-[10px] font-bold text-white">
                {m.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="mt-6 rounded-3xl border border-blue-100 bg-gradient-to-b from-[#EFF6FF] to-white p-4 text-center" data-testid="pro-upsell-card">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-[#2563EB]">
          <Crown className="h-5 w-5 text-white" />
        </div>
        <h3 className="mt-2.5 text-sm font-extrabold text-slate-900">GoSpot Pro</h3>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">{t("Développez votre visibilité et attirez plus de clients.")}</p>
        <button
          type="button"
          data-testid="pro-upsell-button"
          onClick={() => setActive("parametres")}
          className="mt-3 w-full rounded-full border border-[#2563EB] bg-white px-4 py-2 text-xs font-semibold text-[#2563EB] transition hover:bg-[#EFF6FF]"
        >
          {t("Découvrir les avantages")}
        </button>
      </div>
    </div>
  );

  return (
    <main data-testid="pro-dashboard" className="min-h-screen bg-[#F8FAFC]">
      <Seo title={t("Mon établissement — GoSpot Pro")} siteName="GoSpot" description={t("Tableau de bord Pro GoSpot pour les établissements.")} />

      {/* HEADER PRO */}
      <header className="sticky top-0 z-50 border-b border-slate-200/60 bg-white/90 backdrop-blur-md" data-testid="pro-header">
        <div className="mx-auto flex h-16 max-w-[1800px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="pro-drawer-button"
              onClick={() => setDrawer(true)}
              aria-label={t("Menu")}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/" data-testid="pro-logo" className="flex items-center gap-1.5">
              <span className="text-xl font-extrabold tracking-tight text-slate-900">GoSpot</span>
              <span className="rounded-lg bg-[#7C3AED] px-2 py-0.5 text-[11px] font-extrabold text-white">Pro</span>
            </Link>
          </div>

          <nav className="hidden items-center gap-1 xl:flex">
            {PRO_NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`pro-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="rounded-full px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              >
                {t(n.label)}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              data-testid="plan-badge"
              onClick={() => setActive("parametres")}
              className="hidden items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:border-blue-300 md:flex"
            >
              {t("Forfait Pro")}
              <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t("Actif")}
              </span>
              <ChevronRight className="h-3 w-3 text-slate-400" />
            </button>
            <button
              type="button"
              data-testid="pro-city"
              className="hidden items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-medium text-slate-700 sm:flex"
            >
              <MapPin className="h-3.5 w-3.5 text-[#2563EB]" />
              Québec, QC
              <ChevronDown className="h-3 w-3 text-slate-400" />
            </button>
            <button
              type="button"
              data-testid="pro-notifications"
              onClick={() => toast.info(t("3 notifications non lues"))}
              aria-label={t("Notifications")}
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-blue-300"
            >
              <Bell className="h-4.5 w-4.5" size={18} />
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                3
              </span>
            </button>
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                data-testid="pro-profile-menu-button"
                onClick={() => {
                  setProfileMenu((v) => !v);
                  setLangueOpen(false);
                }}
                aria-expanded={profileMenu}
                aria-label={t("Menu du compte")}
                className="flex items-center gap-2.5 rounded-2xl px-1.5 py-1 transition hover:bg-slate-100"
              >
                {user?.avatar ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar}`} alt={user.name} className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2563EB] text-sm font-bold text-white">
                    {user?.name?.[0]?.toUpperCase() || "P"}
                  </span>
                )}
                <div className="hidden text-left sm:block">
                  <p className="text-xs font-bold leading-tight text-slate-900">{user?.name}</p>
                  <p className="text-[10px] font-medium text-[#2563EB]">{t("Propriétaire")}</p>
                </div>
                <ChevronDown className={`hidden h-3.5 w-3.5 text-slate-400 transition-transform sm:block ${profileMenu ? "rotate-180" : ""}`} />
              </button>
              {profileMenu && (
                <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl" data-testid="pro-profile-menu">
                    <ProMenuItem icon={User} label="Mon profil" testId="pro-profile-item-profil" onClick={() => goMenu("/profil")} />
                    <ProMenuItem icon={Building2} label="Mon établissement" testId="pro-profile-item-etablissement" onClick={() => goMenu("/pro/etablissement")} />
                    <ProMenuItem icon={Settings} label="Paramètres" testId="pro-profile-item-parametres" onClick={() => goMenu("/pro/parametres")} />
                    <div>
                      <button
                        type="button"
                        data-testid="pro-profile-item-langue"
                        onClick={() => setLangueOpen((v) => !v)}
                        aria-expanded={langueOpen}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                      >
                        <Globe className="h-4 w-4 text-slate-400" />
                        {t("Langue")}
                        <span className="ml-auto text-xs font-semibold text-slate-400">{lang.toUpperCase()}</span>
                        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${langueOpen ? "rotate-180" : ""}`} />
                      </button>
                      {langueOpen && (
                        <div className="mb-1 ml-9 space-y-0.5" data-testid="pro-profile-item-langue-options">
                          <button
                            type="button"
                            data-testid="pro-profile-item-langue-fr"
                            onClick={() => setLang("fr")}
                            className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition hover:bg-slate-100 ${lang === "fr" ? "font-semibold text-[#2563EB]" : "font-medium text-slate-600"}`}
                          >
                            Français
                            {lang === "fr" && <Check className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            type="button"
                            data-testid="pro-profile-item-langue-en"
                            onClick={() => setLang("en")}
                            className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition hover:bg-slate-100 ${lang === "en" ? "font-semibold text-[#2563EB]" : "font-medium text-slate-600"}`}
                          >
                            English
                            {lang === "en" && <Check className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      )}
                    </div>
                    <ProMenuItem icon={Crown} label="Forfait / abonnement" testId="pro-profile-item-forfait" onClick={goPlan} />
                    <hr className="my-1.5 border-slate-100" />
                    <ProMenuItem
                      icon={Trash2}
                      label="Supprimer mon compte"
                      testId="pro-profile-item-delete"
                      danger
                      onClick={() => {
                        setProfileMenu(false);
                        setDeleteOpen(true);
                      }}
                    />
                    <ProMenuItem icon={LogOut} label="Déconnexion" testId="pro-profile-item-logout" onClick={doLogout} />
                  </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* DRAWER MOBILE */}
      {drawer && (
        <>
          <div className="fixed inset-0 z-[60] bg-slate-900/30 backdrop-blur-sm lg:hidden" onClick={() => setDrawer(false)} />
          <div className="fixed inset-y-0 left-0 z-[70] w-72 overflow-y-auto bg-white p-5 shadow-2xl lg:hidden" data-testid="pro-drawer">
            <button type="button" onClick={() => setDrawer(false)} aria-label={t("Fermer")} className="mb-4 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500">
              <X className="h-4 w-4" />
            </button>
            {sidebar}
          </div>
        </>
      )}

      <DeleteAccountModal open={deleteOpen} onClose={() => setDeleteOpen(false)} />

      <div className="mx-auto flex max-w-[1800px] items-start gap-6 px-4 py-6 sm:px-6 lg:px-10">
        {/* SIDEBAR DESKTOP */}
        <aside className="sticky top-24 hidden w-[250px] shrink-0 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm lg:block" data-testid="pro-sidebar">
          {sidebar}
        </aside>

        {/* CONTENU */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t(SECTION_TITLES[active]?.[0] || "Mon établissement")}</h1>
              <p className="mt-1 text-sm text-slate-500">{t(SECTION_TITLES[active]?.[1] || "")}</p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <button type="button" data-testid="action-edit-info" onClick={openInfoEdit} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-blue-300 hover:text-[#2563EB]">
                <Pencil className="h-3.5 w-3.5" />
                {t("Modifier les infos")}
              </button>
              <button type="button" data-testid="action-add-photos" onClick={() => setActive("photos")} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-blue-300 hover:text-[#2563EB]">
                <ImagePlus className="h-3.5 w-3.5" />
                {t("Ajouter des photos")}
              </button>
              <button type="button" data-testid="action-create-event" onClick={() => setActive("evenements")} className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8]">
                <CalendarDays className="h-3.5 w-3.5" />
                {t("Créer un événement")}
              </button>
              <button type="button" data-testid="action-create-promo" onClick={() => setActive("promotions")} className="flex items-center gap-1.5 rounded-full bg-[#312E81] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#272264]">
                <TagIcon className="h-3.5 w-3.5" />
                {t("Créer une promotion")}
              </button>
            </div>
          </div>

          {active === "apercu" && <ApercuView data={data} go={setActive} />}
          {active === "evenements" && <EventsView est={est} />}
          {active === "reservations" && <ReservationsView />}
          {active === "avis" && <AvisView />}
          {active === "stats" && <StatsView />}
          {active === "contact" && <ContactAdminView />}
          {active === "parametres" && <SettingsView go={setActive} />}
          {active === "photos" && <PhotosView est={est} reload={load} />}
          {active === "horaires" && <HorairesView est={{ ...est, hours_detail: data.hours, special_hours: data.special_hours }} reload={load} />}
          {active === "promotions" && <PromotionsView est={{ ...est, promotions: data.promotions }} reload={load} />}

          {active === "etablissement" && (
          <>
          {/* CARTE ÉTABLISSEMENT + KPI */}
          <div className="mt-6 grid gap-5 xl:grid-cols-[1.1fr_1.4fr]">
            <Card testId="pro-est-card" className="flex flex-col gap-4 sm:flex-row">
              <img src={est.image} alt={est.name} className="h-40 w-full shrink-0 rounded-2xl object-cover sm:w-52" />
              <div className="min-w-0">
                <h2 className="flex items-center gap-1.5 text-lg font-extrabold text-slate-900">
                  {est.name}
                  <BadgeCheck className="h-4 w-4 fill-[#2563EB] text-white" />
                </h2>
                <p className="mt-0.5 text-sm font-medium text-[#2563EB]">{est.category}</p>
                <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {est.address}
                </p>
                <p className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" />
                    {est.phone}
                  </span>
                  <a href="https://novarooftop.ca" onClick={(e) => e.preventDefault()} className="flex items-center gap-1 font-semibold text-[#2563EB] hover:underline" data-testid="est-website">
                    {est.website}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
                <div className="mt-3 flex gap-2">
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">
                    <BadgeCheck className="h-3 w-3" />
                    {t("Vérifié")}
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    {t("Ouvert")}
                  </span>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="pro-kpis">
              {data.kpis.map((k) => {
                const Icon = KPI_ICONS[k.key];
                const [color, bg] = KPI_COLORS[k.key];
                return (
                  <Card key={k.key} testId={`kpi-${k.key}`} className="!p-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-2xl" style={{ backgroundColor: bg }}>
                      <Icon className="h-4.5 w-4.5" size={18} style={{ color }} />
                    </div>
                    <p className="mt-3 text-xs font-medium text-slate-500">{t(k.label)}</p>
                    <p className="mt-0.5 text-xl font-extrabold text-slate-900">{k.value}</p>
                    {k.sub && <p className="text-[10px] text-slate-400">{t(k.sub)}</p>}
                    <p className="mt-0.5 text-[10px] font-semibold text-emerald-600">{lang === "en" ? k.delta.replace("mois dernier", "last month") : k.delta}</p>
                  </Card>
                );
              })}
            </div>
          </div>

          <div className="mt-5 grid gap-5 xl:grid-cols-[1fr_300px]">
            <div className="min-w-0 space-y-5">
              {/* INFOS / HORAIRES / PHOTOS */}
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                <Card testId="pro-info-card">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-900">{t("Informations de l'établissement")}</h3>
                    <button type="button" data-testid="edit-info-button" onClick={openInfoEdit} className="flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline">
                      <Pencil className="h-3 w-3" />
                      {t("Éditer")}
                    </button>
                  </div>
                  <div className="mt-4 space-y-3 text-xs">
                    {[
                      { icon: Building2, label: "Nom", value: est.name },
                      { icon: Tag, label: "Catégorie", value: est.category },
                      { icon: Phone, label: "Téléphone", value: est.phone },
                      { icon: Globe, label: "Site web", value: est.website, link: true },
                      { icon: MapPin, label: "Adresse", value: est.address },
                    ].map((row) => (
                      <div key={row.label} className="flex items-start gap-2.5">
                        <row.icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="w-16 shrink-0 text-slate-400">{t(row.label)}</span>
                        <span className={`font-medium ${row.link ? "text-[#2563EB]" : "text-slate-800"}`}>{row.value}</span>
                      </div>
                    ))}
                    <div className="flex items-start gap-2.5">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                      <span className="w-16 shrink-0 text-slate-400">{t("Description")}</span>
                      <span className="leading-relaxed text-slate-600">{est.description}</span>
                    </div>
                  </div>
                </Card>

                <Card testId="pro-hours-card">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-900">{t("Horaires d'ouverture")}</h3>
                    <button type="button" data-testid="edit-hours-button" onClick={() => setActive("horaires")} className="flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline">
                      <Pencil className="h-3 w-3" />
                      {t("Éditer")}
                    </button>
                  </div>
                  <div className="mt-4 space-y-2.5 text-xs">
                    {data.hours.map((h) => (
                      <div key={h.day} className="flex items-center justify-between">
                        <span className="text-slate-500">{t(h.day)}</span>
                        <span className={`font-medium ${h.value === "Fermé" ? "text-slate-400" : "text-slate-800"}`}>{t(h.value)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 flex items-start gap-1.5 text-[10px] leading-relaxed text-slate-400">
                    <Info className="mt-0.5 h-3 w-3 shrink-0" />
                    {t("Les horaires peuvent varier lors d'événements spéciaux.")}
                  </p>
                </Card>

                <Card testId="pro-photos-card" className="md:col-span-2 xl:col-span-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-900">{t("Galerie photos")}</h3>
                    <button type="button" data-testid="photos-see-all" onClick={() => setActive("photos")} className="flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline">
                      {t("Voir tout")}
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="mt-4 grid grid-cols-4 gap-2.5 xl:grid-cols-2">
                    {photos.slice(0, 3).map((p, i) => (
                      <button key={p + i} type="button" data-testid={`photo-thumb-${i}`} onClick={() => setLightbox(p)} className="overflow-hidden rounded-xl transition hover:opacity-90">
                        <img src={p} alt={`${est.name} — photo ${i + 1}`} className="h-16 w-full object-cover xl:h-20" />
                      </button>
                    ))}
                    <button
                      type="button"
                      data-testid="photo-add-tile"
                      onClick={() => setActive("photos")}
                      className="flex h-16 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 transition hover:border-blue-300 hover:text-[#2563EB] xl:h-20"
                    >
                      <Plus className="h-4 w-4" />
                      <span className="text-[9px] font-semibold">{t("Ajouter")}</span>
                    </button>
                  </div>
                </Card>
              </div>

              {/* ÉVÉNEMENTS À VENIR */}
              <Card testId="pro-events-card">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-900">{t("Événements à venir")}</h3>
                  <button type="button" data-testid="events-see-all" onClick={() => navigate("/evenements")} className="flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline">
                    {t("Voir tout")}
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                <div className="no-scrollbar mt-4 flex snap-x gap-4 overflow-x-auto pb-1">
                  {data.upcoming_events.map((ev, i) => (
                    <button
                      key={ev.title}
                      type="button"
                      data-testid={`pro-event-${i}`}
                      onClick={() => navigate(`/evenement/${ev.slug}`)}
                      className="group w-52 shrink-0 snap-start text-left"
                    >
                      <div className="relative overflow-hidden rounded-2xl">
                        <img src={ev.image} alt={ev.title} className="h-28 w-full object-cover transition duration-500 group-hover:scale-105" />
                        <span className="absolute left-2.5 top-2.5 rounded-lg bg-white px-2 py-1 text-center shadow">
                          <span className="block text-xs font-extrabold leading-none text-slate-900">{ev.chip.day}</span>
                          <span className="block text-[8px] font-bold uppercase text-slate-400">
                            {lang === "en" && ev.date ? new Intl.DateTimeFormat("en-CA", { month: "short" }).format(new Date(`${ev.date}T12:00:00`)) : ev.chip.month}
                          </span>
                        </span>
                      </div>
                      <p className="mt-2 truncate text-sm font-semibold text-slate-900">{ev.title}</p>
                      <p className="text-xs text-slate-400">{ev.when}</p>
                      <p className="mt-0.5 text-xs font-semibold text-[#2563EB]">{t(ev.tag)}</p>
                    </button>
                  ))}
                </div>
              </Card>

              {/* RÉSERVATIONS + AVIS */}
              <div className="grid gap-5 xl:grid-cols-2">
                <Card testId="pro-reservations-card">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-900">{t("Réservations récentes")}</h3>
                    <button type="button" data-testid="reservations-see-all" onClick={() => setActive("reservations")} className="flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline">
                      {t("Voir toutes")}
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="text-slate-400">
                          <th className="pb-2 pr-3 font-medium">{t("Client")}</th>
                          <th className="pb-2 pr-3 font-medium">{t("Date")}</th>
                          <th className="pb-2 pr-3 font-medium">{t("Heure")}</th>
                          <th className="pb-2 pr-3 font-medium">{t("Personnes")}</th>
                          <th className="pb-2 font-medium">{t("Statut")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.reservations.map((r, i) => (
                          <tr
                            key={r.client}
                            data-testid={`pro-reservation-${i}`}
                            onClick={() => toast.info(lang === "en" ? `${r.client}'s reservation — details coming soon` : `Réservation de ${r.client} — détail à venir`)}
                            className="cursor-pointer border-t border-slate-50 transition hover:bg-slate-50/60"
                          >
                            <td className="py-2.5 pr-3">
                              <span className="flex items-center gap-2">
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EFF6FF] text-[10px] font-bold text-[#2563EB]">
                                  {r.client[0]}
                                </span>
                                <span className="font-semibold text-slate-800">{r.client}</span>
                              </span>
                            </td>
                            <td className="py-2.5 pr-3 text-slate-500">{r.date}</td>
                            <td className="py-2.5 pr-3 text-slate-500">{r.time}</td>
                            <td className="py-2.5 pr-3 text-slate-500">{r.guests}</td>
                            <td className="py-2.5">
                              <span className={`flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ${r.status === "Confirmée" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${r.status === "Confirmée" ? "bg-emerald-500" : "bg-amber-500"}`} />
                                {t(r.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card testId="pro-reviews-card">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-900">{t("Avis récents")}</h3>
                    <button type="button" data-testid="reviews-see-all" onClick={() => setActive("avis")} className="flex items-center gap-1 text-xs font-semibold text-[#2563EB] hover:underline">
                      {t("Voir tous les avis")}
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="mt-4 space-y-4">
                    {data.reviews.length === 0 && (
                      <p className="rounded-2xl bg-[#F8FAFC] p-6 text-center text-xs text-slate-400">{t("Aucun avis pour le moment.")}</p>
                    )}
                    {data.reviews.map((r, i) => (
                      <div key={r.id} data-testid={`pro-review-${i}`} className="rounded-2xl border border-slate-50 p-3.5">
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EFF6FF] text-xs font-bold text-[#2563EB]">
                            {r.name?.[0]?.toUpperCase() || "A"}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-slate-900">{r.name}</p>
                            <Stars value={r.rating} />
                          </div>
                          <span className="text-[10px] text-slate-400">{r.date}</span>
                        </div>
                        <p className="mt-2.5 text-xs leading-relaxed text-slate-600">{r.comment}</p>
                        {r.reply && (
                          <div className="mt-2.5 rounded-xl bg-[#EFF6FF] p-3" data-testid={`pro-review-reply-${i}`}>
                            <p className="text-[10px] font-bold text-[#2563EB]">{t("Réponse du propriétaire")}</p>
                            <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{r.reply.text}</p>
                          </div>
                        )}
                        {replyFor === r.id ? (
                          <div className="mt-2.5 flex gap-2">
                            <input
                              data-testid={`reply-input-${i}`}
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder={t("Ta réponse…")}
                              className="flex-1 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs outline-none focus:border-[#2563EB]"
                            />
                            <button
                              type="button"
                              data-testid={`reply-send-${i}`}
                              onClick={() => submitReply(r.id)}
                              className="rounded-full bg-[#2563EB] px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-[#1D4ED8]"
                            >
                              {t("Envoyer")}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            data-testid={`reply-open-${i}`}
                            onClick={() => {
                              setReplyFor(r.id);
                              setReplyText(r.reply?.text || "");
                            }}
                            className="mt-2 text-[11px] font-semibold text-[#2563EB] hover:underline"
                          >
                            {r.reply ? t("Modifier la réponse") : t("Répondre")}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>

            {/* COLONNE DROITE */}
            <div className="space-y-5">
              <Card testId="quick-actions-card">
                <h3 className="flex items-center justify-between text-sm font-bold text-slate-900">
                  {t("Actions rapides")}
                  <Zap className="h-4 w-4 text-[#7C3AED]" />
                </h3>
                <div className="mt-3 space-y-1">
                  {[
                    { icon: MessageSquare, title: "Répondre aux avis", sub: "Réponds à tes clients", action: scrollToReviews, id: "reply-reviews" },
                    { icon: Pencil, title: "Mettre à jour les infos", sub: "Gardez vos infos à jour", action: openInfoEdit, id: "update-info" },
                    { icon: Tag, title: "Créer une promotion", sub: "Attirez plus de clients", action: () => setActive("promotions"), id: "create-promo" },
                    { icon: Share2, title: "Partager votre page", sub: "Augmentez votre visibilité", action: sharePage, id: "share-page" },
                  ].map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      data-testid={`quick-${a.id}`}
                      onClick={a.action}
                      className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition hover:bg-slate-50"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EFF6FF]">
                        <a.icon className="h-4 w-4 text-[#2563EB]" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-semibold text-slate-800">{t(a.title)}</span>
                        <span className="block truncate text-[10px] text-slate-400">{t(a.sub)}</span>
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
                    </button>
                  ))}
                </div>
              </Card>

              <Card testId="tips-card">
                <h3 className="flex items-center justify-between text-sm font-bold text-slate-900">
                  {t("Conseils de performance")}
                  <Sparkles className="h-4 w-4 text-[#7C3AED]" />
                </h3>
                <div className="mt-3 rounded-2xl bg-[#F8FAFC] p-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EFF6FF]">
                    <CalendarDays className="h-4 w-4 text-[#2563EB]" />
                  </div>
                  <p className="mt-3 text-xs font-bold text-slate-900">{t("Publiez un événement")}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    {t("Les établissements qui publient régulièrement des événements attirent 3x plus de vues.")}
                  </p>
                  <button
                    type="button"
                    data-testid="tips-create-event"
                    onClick={() => navigate("/publier")}
                    className="mt-3 text-xs font-semibold text-[#2563EB] hover:underline"
                  >
                    {t("Créer un événement")}
                  </button>
                </div>
              </Card>
            </div>
          </div>
          </>
          )}
        </div>
      </div>

      {/* LIGHTBOX PHOTO */}
      {lightbox && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <img src={lightbox} alt={t("Photo agrandie")} className="max-h-[85vh] max-w-full rounded-3xl object-contain shadow-2xl" />
          <button type="button" aria-label={t("Fermer")} className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* MODALE ÉDITION INFOS */}
      {editInfo && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" onClick={() => setEditInfo(false)} data-testid="edit-info-modal">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900">{t("Modifier les informations")}</h3>
              <button type="button" onClick={() => setEditInfo(false)} aria-label={t("Fermer")} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:text-slate-800">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid="edit-info-phone" value={infoForm.phone} onChange={(e) => setInfoForm((f) => ({ ...f, phone: e.target.value }))} placeholder={t("Téléphone")} className="w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-[#2563EB]" />
                <input data-testid="edit-info-website" value={infoForm.website} onChange={(e) => setInfoForm((f) => ({ ...f, website: e.target.value }))} placeholder={t("Site web")} className="w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-[#2563EB]" />
              </div>
              <input data-testid="edit-info-address" value={infoForm.address} onChange={(e) => setInfoForm((f) => ({ ...f, address: e.target.value }))} placeholder={t("Adresse")} className="w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-[#2563EB]" />
              <input data-testid="edit-info-category" value={infoForm.category_label} onChange={(e) => setInfoForm((f) => ({ ...f, category_label: e.target.value }))} placeholder={t("Catégorie (ex. : Bar à cocktails)")} className="w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-[#2563EB]" />
              <textarea data-testid="edit-info-description" rows={4} value={infoForm.description} onChange={(e) => setInfoForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("Description")} className="w-full resize-none rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-[#2563EB]" />
              <div className="flex justify-end gap-2.5 pt-1">
                <button type="button" data-testid="edit-info-cancel" onClick={() => setEditInfo(false)} className="rounded-full border border-slate-200 px-5 py-2.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
                  {t("Annuler")}
                </button>
                <button type="button" data-testid="edit-info-save" onClick={saveInfo} disabled={savingInfo} className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-[#1D4ED8] disabled:opacity-60">
                  {savingInfo ? t("Enregistrement…") : t("Enregistrer")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
